import { useCallback } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import {
  ConnectedPlatform,
  ConnectedPlatformsResponse,
  ConnectSocialAccountResponse,
  AccountInsights,
  AccountInsightsResponse,
} from "../types/social-connect";

export const usePfmSocialConnectApi = () => {
  const { post, get, del } = useHttpAdapter();

  const createProfilePfm = useCallback(
    async (
      brandId: string,
      brandName: string
    ): Promise<Record<string, any> | undefined> => {
      try {
        const url = "/pfm/connectSocial/createProfile";
        const payload = {
          name: brandName,
          desc: `${brandName}'s social profile`,
          product_id: brandId,
        };

        const response = await post<{
          status: string;
          profile: Record<string, any>;
        }>(url, payload);
        if (response.status === 201) {
          return response.data.profile;
        }
        return undefined;
      } catch (e) {
        return undefined;
      }
    },
    [post]
  );

  const connectPlatformPfm = useCallback(
    async (
      platformName: string,
      brandId: string
    ): Promise<ConnectSocialAccountResponse | undefined> => {
      try {
        const url = "/pfm/connectSocial/connect";
        const payload = {
          brand_id: brandId,
          platform: platformName,
        };

        const response = await post<ConnectSocialAccountResponse>(url, payload);

        if (response.status >= 200 && response.status < 300) {
          return response.data;
        }
      } catch (e) {
        console.error(e);
      }
    },
    [post]
  );

  const getConnectedPlatformsPfm = useCallback(
    async (productId: string): Promise<ConnectedPlatform[]> => {
      try {
        const url = `/pfm/connectSocial/connectedPlatforms/${productId}`;

        const response = await get<ConnectedPlatformsResponse>(url);
        if (response.status === 200) {
          return response.data.profile?.accounts ?? [];
        }
        return [];
      } catch (e) {
        return [];
      }
    },
    [get]
  );

  const disconnectPlatformPfm = useCallback(
    async (accountId: string, productId: string): Promise<boolean> => {
      try {
        const url = "/pfm/connectSocial/disconnect";
        const response = await del<{ status?: string }>(url, {
          data: {
            account_id: accountId,
            product_id: productId,
          },
        });

        return response.status >= 200 && response.status < 300;
      } catch (e) {
        return false;
      }
    },
    [del]
  );

  const getAccountInsightsPfm = useCallback(
    async (accountId: string): Promise<AccountInsights | undefined> => {
      try {
        const url = `/pfm/connectSocial/insights/${accountId}`;
        const response = await get<AccountInsightsResponse>(url);
        if (response.status === 200) {
          return response.data.insights;
        }
        return undefined;
      } catch (e) {
        console.error("[PFM Insights] Error fetching insights:", e);
        return undefined;
      }
    },
    [get]
  );

  return {
    createProfilePfm,
    connectPlatformPfm,
    getConnectedPlatformsPfm,
    disconnectPlatformPfm,
    getAccountInsightsPfm,
  };
};
